/**
 * Dialog para exibir detalhes das execuções de fluxos
 * Mostra listagem com informações de contato, erros, e nó que falhou
 * Inclui botão "Ver Chat" para abrir conversa associada ao erro
 * 
 * @release v2.0.0 - 2026-03-26 - Adicionado botão Ver Chat
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  User,
  Phone,
  Workflow,
  Zap,
  XCircle,
  Loader2,
  AlertTriangle,
  MessageCircle,
} from 'lucide-react';

// Tipos para os dados de execução
interface ExecutionData {
  id: string;
  flow_id: string;
  status: string;
  trigger_type: string;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  current_node_id: string | null;
  context: Record<string, unknown>;
  flow_name: string;
  contact_name: string | null;
  contact_phone: string | null;
  conversation_id: string | null;
}

// Props do componente - tenantId removido para segurança (obtido no backend)
interface ExecutionDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'today' | 'errors';
}

// Configuração de status para badges
const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2 }> = {
  completed: { label: 'Concluído', variant: 'default', icon: CheckCircle2 },
  running: { label: 'Executando', variant: 'secondary', icon: Loader2 },
  failed: { label: 'Falhou', variant: 'destructive', icon: XCircle },
  paused: { label: 'Pausado', variant: 'outline', icon: Clock },
  waiting: { label: 'Aguardando', variant: 'outline', icon: Clock },
};

// Mapeamento de tipos de gatilho
const triggerTypeLabels: Record<string, string> = {
  any_message: 'Qualquer mensagem',
  keyword: 'Palavra-chave',
  first_contact: 'Primeiro contato',
  manual: 'Manual',
  scheduled: 'Agendado',
  webhook: 'Webhook',
  api: 'API',
};

/**
 * Formata UUID para exibir apenas os 8 primeiros caracteres em maiúsculas
 * Padrão visual consistente com a página de inbox
 * @param uuid UUID completo a ser formatado
 * @returns UUID formatado (8 caracteres maiúsculos)
 */
const formatUuid = (uuid: string): string => {
  if (!uuid) return '';
  return uuid.substring(0, 8).toUpperCase();
};

/**
 * Componente principal do dialog de detalhes de execução
 */
export function ExecutionDetailsDialog({
  open,
  onOpenChange,
  mode,
}: ExecutionDetailsDialogProps) {
  // Estado para controlar quais itens estão expandidos
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  // Estado para controlar o modal de chat
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);

  // Query para buscar execuções usando RPC com UUIDs sanitizados no backend
  // tenant_id é obtido internamente pela RPC via auth.uid() - não exposto no network
  const { data: executions, isLoading } = useQuery({
    queryKey: ['flow-executions-details', mode],
    queryFn: async (): Promise<ExecutionData[]> => {
      logger.debug('ExecutionDetailsDialog', 'Buscando execuções via RPC (tenant obtido no backend)', mode);

      // RPC obtém tenant internamente via get_validated_tenant_id()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .rpc('get_flow_executions_sanitized', {
          p_mode: mode,
          p_limit: 100
        });

      if (error) {
        logger.error('ExecutionDetailsDialog', 'Erro ao buscar execuções', error.message);
        throw error;
      }

      // Mapear dados para formato esperado
      return (data || []).map((item: Record<string, unknown>) => ({
        id: item.id as string,
        flow_id: item.flow_id as string,
        status: item.status as string,
        trigger_type: item.trigger_type as string,
        started_at: item.started_at as string,
        completed_at: item.completed_at as string | null,
        error_message: item.error_message as string | null,
        current_node_id: item.current_node_id as string | null,
        context: (item.context as Record<string, unknown>) || {},
        flow_name: (item.flow_name as string) || 'Fluxo desconhecido',
        contact_name: item.contact_name as string | null,
        contact_phone: item.contact_phone as string | null,
        conversation_id: item.conversation_id as string | null,
      }));
    },
    enabled: open,
    refetchInterval: open ? 10000 : false, // Atualiza a cada 10s quando aberto
  });

  // Alternar expansão de um item
  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Calcular duração da execução
  const calculateDuration = (started: string, completed: string | null): string => {
    if (!completed) return '-';
    const start = new Date(started).getTime();
    const end = new Date(completed).getTime();
    const diffMs = end - start;
    
    if (diffMs < 1000) return `${diffMs}ms`;
    if (diffMs < 60000) return `${Math.round(diffMs / 1000)}s`;
    return `${Math.round(diffMs / 60000)}min`;
  };

  // Título do dialog baseado no modo
  const dialogTitle = mode === 'errors' 
    ? 'Execuções com Erros (Hoje)' 
    : 'Execuções de Hoje';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'errors' ? (
              <AlertTriangle className="h-5 w-5 text-destructive" />
            ) : (
              <Workflow className="h-5 w-5 text-primary" />
            )}
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[60vh] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Carregando execuções...</span>
            </div>
          ) : !executions?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                {mode === 'errors' 
                  ? 'Nenhuma execução com erro hoje!' 
                  : 'Nenhuma execução registrada hoje.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {executions.map((execution) => {
                const isExpanded = expandedItems.has(execution.id);
                const statusInfo = statusConfig[execution.status] || statusConfig.failed;
                const StatusIcon = statusInfo.icon;

                return (
                  <Collapsible
                    key={execution.id}
                    open={isExpanded}
                    onOpenChange={() => toggleExpand(execution.id)}
                  >
                    <div className={`border rounded-lg p-4 transition-colors ${
                      execution.status === 'failed' 
                        ? 'border-destructive/30 bg-destructive/5' 
                        : 'border-border'
                    }`}>
                      {/* Cabeçalho do item */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Nome do fluxo e status */}
                          <div className="flex items-center gap-2 mb-2">
                            <Workflow className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="font-medium truncate">{execution.flow_name}</span>
                            <Badge variant={statusInfo.variant} className="shrink-0">
                              <StatusIcon className={`h-3 w-3 mr-1 ${execution.status === 'running' ? 'animate-spin' : ''}`} />
                              {statusInfo.label}
                            </Badge>
                          </div>

                          {/* Informações do contato */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            {execution.contact_name && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {execution.contact_name}
                              </span>
                            )}
                            {execution.contact_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {execution.contact_phone}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Zap className="h-3 w-3" />
                              {triggerTypeLabels[execution.trigger_type] || execution.trigger_type}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(new Date(execution.started_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                            </span>
                            {execution.completed_at && (
                              <span className="text-xs">
                                ({calculateDuration(execution.started_at, execution.completed_at)})
                              </span>
                            )}
                          </div>

                          {/* Mensagem de erro (sempre visível para erros) */}
                          {execution.error_message && (
                            <div className="mt-3 p-2 rounded bg-destructive/10 border border-destructive/20">
                              <div className="flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                                <div className="text-sm">
                                  <p className="text-destructive font-medium">Erro:</p>
                                  <p className="text-destructive/90 break-words">
                                    {execution.error_message}
                                  </p>
                                  {execution.current_node_id && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Node ID: <code className="bg-muted px-1 rounded">{execution.current_node_id}</code>
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Botões de ação: Ver Chat + Expandir */}
                        <div className="flex items-center gap-1 shrink-0">
                          {/* Botão Ver Chat */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  disabled={!execution.conversation_id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (execution.conversation_id) {
                                      setChatConversationId(execution.conversation_id);
                                    }
                                  }}
                                >
                                  <MessageCircle className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {execution.conversation_id ? 'Ver chat da conversa' : 'Sem conversa vinculada'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          {/* Botão expandir */}
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="shrink-0">
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                      </div>

                      {/* Conteúdo expandido */}
                      <CollapsibleContent>
                        <div className="mt-4 pt-4 border-t space-y-3">
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">ID da Execução:</p>
                            <code className="text-xs bg-muted px-2 py-1 rounded block truncate">
                              {formatUuid(execution.id)}
                            </code>
                          </div>
                          
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Contexto:</p>
                            <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-40">
                              {JSON.stringify(execution.context, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>

      {/* Modal de Chat - renderizado fora do DialogContent para evitar conflito de z-index */}
      <ChatConversationModal
        conversationId={chatConversationId}
        open={!!chatConversationId}
        onOpenChange={(open) => {
          if (!open) setChatConversationId(null);
        }}
      />
    </Dialog>
  );
}
